import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Icon from '../Icon2/Icon';
import Heading from '../Typography/Heading/Heading';
import Button from '../Button/Button';
import './Popover.css';

interface PopoverProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
  children: React.ReactNode;
  popoverWidth?: number;
  boundaryRef?: React.RefObject<HTMLElement>;
  offset?: number;
  hasHeader?: boolean;
  header?: React.ReactNode;
  headerTitle?: string;
  headerBackground?: string;
  isDismissable?: boolean;
  onDismiss?: () => void;
  isStickyHeader?: boolean;
  hasFooter?: boolean;
  footer?: React.ReactNode;
  cta1?: () => void;
  cta2?: () => void;
  footerLabels?: string[];
  isStickyFooter?: boolean;
  dismissOnClickOutside?: boolean;
  dismissOnEscapeKey?: boolean;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  ariaDescription?: string;
  testId?: string;
}

interface DefaultHeaderProps {
  headerTitle?: string,
  headerBackground?: string,
  isDismissable?: boolean,
  onDismiss?: () => void,
  isStickyHeader?: boolean
}

interface DefaultFooterProps {
  cta1?: () => void,
  cta2?: () => void,
  footerLabels?: string[],
  isStickyFooter?: boolean
}

const Popover: React.FC<PopoverProps> = ({
  isOpen,
  onClose,
  anchorRef,
  children,
  popoverWidth = 300,
  boundaryRef,
  offset = 8,
  hasHeader = false,
  header,
  headerTitle,
  headerBackground,
  isDismissable = false,
  onDismiss,
  isStickyHeader = false,
  hasFooter = false,
  footer,
  cta1,
  cta2,
  footerLabels,
  isStickyFooter = false,
  dismissOnClickOutside = true,
  dismissOnEscapeKey = false,
  ariaLabelledBy,
  ariaDescribedBy,
  ariaDescription,
  testId,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, maxHeight: 0 });

  const updatePosition = () => {
    if (!anchorRef.current || !popoverRef.current) return;

    const triggerRect = anchorRef.current.getBoundingClientRect();
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    const boundaryRect = boundaryRef?.current?.getBoundingClientRect();
    const popoverHeight = popoverRef.current.offsetHeight;

    // Available height for scroll
    const maxHeight = (window.visualViewport?.height ?? window.innerHeight) - 50;

    const viewPortHeight = window.visualViewport?.height ?? window.innerHeight;

    let top = triggerRect.top + scrollY;
    const isPopoverOutOfViewport = triggerRect.top + popoverHeight > viewPortHeight;
    if (isPopoverOutOfViewport) {
      top = top + (viewPortHeight - (popoverHeight + triggerRect.top + 20)); //20 added so that the popover is not sticking way bottom of the screen when opened
    }

    let left: number;

    const boundaryLeft = boundaryRect ? boundaryRect.left + scrollX : 0;
    const hasSpaceLeftOfBoundary = boundaryLeft - popoverWidth - offset >= 0;
    const hasSpaceRightOfButton = triggerRect.right + popoverWidth + offset <= window.innerWidth;

    if (hasSpaceLeftOfBoundary) {
      left = boundaryLeft - popoverWidth - offset;
    } else if (hasSpaceRightOfButton) {
      left = triggerRect.right + scrollX + offset;
    } else {
      left = triggerRect.left + scrollX;
    }

    setCoords({ top, left, maxHeight });
  };

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    // Update position on resize or scroll
    // UnComment it when this support is needed

    // window.addEventListener('resize', updatePosition);
    // window.addEventListener('scroll', updatePosition, true);

    // return () => {
    // window.removeEventListener('resize', updatePosition);
    // window.removeEventListener('scroll', updatePosition, true);
    // };
  }, [isOpen, anchorRef, boundaryRef, popoverWidth, popoverRef.current]);

  // Watch for content size changes and update position accordingly
  useEffect(() => {
    if (!isOpen || !contentRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      // Add a small delay to ensure DOM has updated
      setTimeout(() => {
        updatePosition();
      }, 0);
    });

    resizeObserver.observe(contentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isOpen, children]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e?.target as Element;

      if (target?.closest?.('.ReactModalPortal')) return;
      // A nested Popover (e.g. a Select choice-menu opened inside a Group's contextual
      // popover) is portaled to <body> as a sibling, not a DOM descendant. Without this
      // guard its mousedown reads as an "outside" click and dismisses the ancestor
      // popover before the nested control's onClick can commit. Mirrors the modal guard above.
      if (target?.closest?.('.popover')) return;
      // Same reasoning for a react-select menu portaled out of a popover: venus Select
      // uses the "Portal" classNamePrefix when menuPortalTarget is set, so its menu lands
      // in a `.Portal__menu-portal` sibling of this popover.
      if (target?.closest?.('.Portal__menu-portal')) return;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        !anchorRef.current?.contains(e.target as Node) &&
        dismissOnClickOutside
      ) {
        onClose();
      }
    };

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && dismissOnEscapeKey) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      ref={popoverRef}
      data-test-id={testId}
      className="popover fade-in"
      role="region"
      aria-labelledby={ariaLabelledBy || (hasHeader ? "popover-header-title" : undefined)}
      aria-describedby={ariaDescribedBy}
      style={{
        position: 'absolute',
        top: `${coords.top}px`,
        left: `${coords.left}px`,
        maxHeight: `${coords.maxHeight}px`,
        width: `${popoverWidth}px`,
      }}
    >
      {hasHeader ? (header ? header : <DefaultHeader isDismissable={isDismissable} headerTitle={headerTitle} headerBackground={headerBackground} onDismiss={onDismiss} isStickyHeader={isStickyHeader} />) : null}
      <div ref={contentRef} className="popover__content">{children}</div>
      {hasFooter ? (footer ? footer : <DefaultFooter cta1={cta1} cta2={cta2} footerLabels={footerLabels} isStickyFooter={isStickyFooter} />) : null}
      {ariaDescription && (
        <div id={ariaDescribedBy} className="sr-only">
          {ariaDescription}
        </div>
      )}
    </div>,
    // document.getElementById('root')
    document.body
  );
};

const DefaultHeader = ({ headerTitle, headerBackground, isDismissable, onDismiss, isStickyHeader }: DefaultHeaderProps) => {
  return (
    <div className={`popover__header ${isStickyHeader ? 'sticky' : ''}`} style={{ backgroundColor: headerBackground }} id="popover-header-title">
      <Heading tagName='h6' text={headerTitle || 'Settings'} className='popover__header-title' />
      {
        isDismissable &&
        <Button buttonType='tertiary' size='small' className='popover__dismissable-button' onClick={onDismiss}>
          <Icon icon='CloseNoborder' size='small' version='v2' className='dismissable' />
        </Button>
      }
    </div>
  )
}

const DefaultFooter = ({ cta1, cta2, footerLabels, isStickyFooter }: DefaultFooterProps) => {
  return (
    <div className={`popover__footer ${isStickyFooter ? 'sticky' : ''}`}>
      <Button buttonType='tertiary' size='small' className='popover__footer-button' onClick={cta1}>{footerLabels?.[0] || 'Cancel'}</Button>
      <Button buttonType='primary' size='small' className='popover__footer-button' onClick={cta2}>{footerLabels?.[1] || 'Insert'}</Button>
    </div>
  )
}

export default Popover;
